import parser from './jison-ldpatch-parser';
import { LDadd, BlankNode, LDdeleteResource, LDinsertResource, LDreplace, LDupdateList, LDdelete, LDupdate, LDquad, LDnoop, Literal, URI } from './ld-patch-model';

function LdPatchParser() {

	// We use a JISON parser to parse the LD Patch syntax locally and execute accordingly
	// Currently we're only supporting string literal with no language or datatype bits

	let parseItems = [];

	this.createLiteral = function (text, language, datatype) {
		return new Literal(text, language, datatype);
	};

	this.createURI = function (uri) {
		return new URI(uri);
	};

	this.createLDnoop = function () {
		return new LDnoop();
	};

	this.createLDdeleteResource = function () {
		return new LDdeleteResource();
	};

	this.createLDinsertResource = function () {
		return new LDinsertResource();
	};

	this.createLDupdate = function () {
		return new LDupdate();
	};

	this.createLDreplace = function () {
		return new LDreplace();
	};

	this.createLDupdateList = function () {
		return new LDupdateList();
	};

	this.createLDadd = function () {
		return new LDadd();
	};

	this.createLDdelete = function () {
		return new LDdelete();
	};

	function executeAddNoop() {
		parseItems.push(new LDnoop());
	}

	function executeInsertResource(data) {
		let iri = "";
		const ldir = new LDinsertResource();
		const quads = data.map(triple => {
			// Each triple is an array
			let s;
			if (typeof triple[0] === "string") {
				s = new URI(triple[0]);
				if (iri === "") {
					iri = s.getText();
					ldir.setIRI(new URI(iri));
				}
				else {
					if (s.getText() !== iri) throw new Error("Quad data for resource insertion references more than one resource: " + iri + " : " + s.getText());
				}
			}
			// Blank node
			else {
				s = new BlankNode(triple[0][1]);
			}
			const p = new URI(triple[1]);
			let o;
			const oArray = triple[2];
			if (typeof oArray === "string") {
				o = new URI(oArray);
			}
			else if (oArray[0] === "BlankNode") {
				o = new BlankNode(oArray[1]);
			}
			else {
				o = new Literal(oArray[1], oArray[2], oArray[3]);
			}
			return new LDquad(s, p, o);
		});
		ldir.setData(quads);
		parseItems.push(ldir);
	}

	function executeDeleteResource(iriText) {
		const lddr = new LDdeleteResource();
		lddr.setIRI(new URI(iriText));
		parseItems.push(lddr);
	}

	function executeAddLiteralUpdate(subjectText, predicateText, object) {
		const lda = new LDadd();
		lda.setSubject(new URI(subjectText));
		lda.setPredicate(new URI(predicateText));
		lda.setObject(new Literal(object[1], object[2], object[3]));
		parseItems.push(lda);
	}

	function executeAddIRIUpdate(subjectText, predicateText, objectText) {
		const lda = new LDadd();
		lda.setSubject(new URI(subjectText));
		lda.setPredicate(new URI(predicateText));
		lda.setObject(new URI(objectText));
		parseItems.push(lda);
	}

	function executeDeleteLiteralUpdate(subjectText, predicateText, object) {
		const ldd = new LDdelete();
		ldd.setSubject(new URI(subjectText));
		ldd.setPredicate(new URI(predicateText));
		ldd.setObject(new Literal(object[1], object[2], object[3]));
		parseItems.push(ldd);
	}

	function executeDeleteIRIUpdate(subjectText, predicateText, objectText) {
		const ldd = new LDdelete();
		ldd.setSubject(new URI(subjectText));
		ldd.setPredicate(new URI(predicateText));
		ldd.setObject(new URI(objectText));
		parseItems.push(ldd);
	}

	function executeUpdateLiteralLiteralUpdate(subjectText, predicateText, object, replacement) {
		const ldu = new LDupdate();
		ldu.setSubject(new URI(subjectText));
		ldu.setPredicate(new URI(predicateText));
		ldu.setObject(new Literal(object[1], object[2], object[3]));
		ldu.setObjectReplacement(new Literal(replacement[1], replacement[2], replacement[3]));
		parseItems.push(ldu);
	}

	function executeUpdateIRIIRIUpdate(subjectText, predicateText, objectText, replacementText) {
		const ldu = new LDupdate();
		ldu.setSubject(new URI(subjectText));
		ldu.setPredicate(new URI(predicateText));
		ldu.setObject(new URI(objectText));
		ldu.setObjectReplacement(new URI(replacementText));
		parseItems.push(ldu);
	}

	function executeReplaceLiteralIRIReplace(subjectText, predicateText, object, predicateReplacementText, replacementText) {
		const ldr = new LDreplace();
		ldr.setSubject(new URI(subjectText));
		ldr.setPredicate(new URI(predicateText));
		ldr.setPredicateReplacement(new URI(predicateReplacementText));
		ldr.setObject(new Literal(object[1], object[2], object[3]));
		ldr.setObjectReplacement(new URI(replacementText));
		parseItems.push(ldr);
	}

	function executeReplaceIriLiteralReplace(subjectText, predicateText, objectText, predicateReplacementText, replacement) {
		const ldr = new LDreplace();
		ldr.setSubject(new URI(subjectText));
		ldr.setPredicate(new URI(predicateText));
		ldr.setPredicateReplacement(new URI(predicateReplacementText));
		ldr.setObject(new URI(objectText));
		ldr.setObjectReplacement(new Literal(replacement[1], replacement[2], replacement[3]));
		parseItems.push(ldr);
	}

	function executeReplaceIRIIRIReplace(subjectText, predicateText, objectText, predicateReplacementText, replacementText) {
		const ldr = new LDreplace();
		ldr.setSubject(new URI(subjectText));
		ldr.setPredicate(new URI(predicateText));
		ldr.setPredicateReplacement(new URI(predicateReplacementText));
		ldr.setObject(new URI(objectText));
		ldr.setObjectReplacement(new URI(replacementText));
		parseItems.push(ldr);
	}

	// List will come in as an array of items
	function executeUpdateListUpdate(subjectText, predicateText, slice, list) {

		// Expecting list as an array of IRIs or strings

		let sliceBegin = -1;
		let sliceEnd = -1;
		// Either start or end of slice not given
		if (slice.startsWith("..")) {
			if (!slice.endsWith("..")) {
				sliceEnd = parseInt(slice.substring(2));
			}
		} else {
			sliceBegin = parseInt(slice.substring(0, slice.indexOf("..")));
			if (!slice.endsWith("..")) {
				sliceEnd = parseInt(slice.substring(slice.indexOf("..") + 2, slice.length))
			}
		}

		const replacementList = list.map((item) => {
			if (typeof item === "string") {
				return new URI(item);
			} else {
				return new Literal(item[1], item[2], item[3]);
			}
		});

		const ldu = new LDupdateList();
		ldu.setSubject(new URI(subjectText));
		ldu.setPredicate(new URI(predicateText));
		ldu.setStartIndex(sliceBegin);
		ldu.setEndIndex(sliceEnd);
		ldu.setReplacementList(replacementList);
		parseItems.push(ldu);
	}

	// Expecting a JISON parser to be available which exposes parser.yy for use
	parser.yy = {
		addNoop: function () {
			executeAddNoop();
		},
		insertResource: function (t) {
			executeInsertResource(t);
		},
		deleteResource: function (t) {
			executeDeleteResource(t);
		},
		addLiteral: function (s, p, o) {
			executeAddLiteralUpdate(s, p, o);
		},
		addIri: function (s, p, o) {
			executeAddIRIUpdate(s, p, o);
		},
		deleteLiteral: function (s, p, o) {
			executeDeleteLiteralUpdate(s, p, o);
		},
		deleteIri: function (s, p, o) {
			executeDeleteIRIUpdate(s, p, o);
		},
		updateIriIri: function (s, p, o, r) {
			executeUpdateIRIIRIUpdate(s, p, o, r);
		},
		updateLiteralLiteral: function (s, p, o, r) {
			executeUpdateLiteralLiteralUpdate(s, p, o, r);
		},
		replaceLiteralIri: function (s, p, o, q, r) {
			executeReplaceLiteralIRIReplace(s, p, o, q, r);
		},
		replaceIriLiteral: function (s, p, o, q, r) {
			executeReplaceIriLiteralReplace(s, p, o, q, r);
		},
		replaceIriIri: function (s, p, o, q, r) {
			executeReplaceIRIIRIReplace(s, p, o, q, r);
		},
		updateList: function (s, p, slice, list) {
			executeUpdateListUpdate(s, p, slice, list);
		}
	};

	this.getObjects = function (message, graphIRI, propertyGraphIRI) {
		parseItems = [];
		parser.parse(message);
		parseItems.forEach(item => {
			if (item.constructor !== LDnoop) {
				item.setGraph(new URI(graphIRI));
				if (propertyGraphIRI) {
					item.setPredicateGraph(new URI(propertyGraphIRI));
				}
			}
		});
		return parseItems;
	};

}

export default LdPatchParser;